/**
 * Backtest a Baseline Accounting project against a year it already knows the answer to.
 *
 * The year is held out in the workbook: its figures become the year before's, it is
 * relabelled a forecast year, and the workbook's own forecast rules produce it. The
 * comparison is IAS accounting's, so a backtest reads the same in both disciplines. The
 * notes come ABOVE the numbers: carrying a year forward is a naive baseline, and a large
 * revenue error says nothing about the model - a reader should know that first.
 */

import { Fragment, useEffect, useState } from 'react';

import { useMAccountingStore } from '../../../store/m-accounting';
import type { BacktestRow } from '../../../types/financial-model-v2';
import { Markdown } from '../Markdown';
import { Button } from '../ui/Button';

function money(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Toned by the SIZE of the error, not its direction - over and under are both misses. */
function tone(pct: number | null): string {
  if (pct === null) return 'text-gray-400';
  const m = Math.abs(pct);
  if (m < 0.05) return 'text-green-700';
  if (m < 0.15) return 'text-amber-700';
  return 'text-red-700';
}

export function MBacktest() {
  const years = useMAccountingStore((s) => s.backtestYears);
  const result = useMAccountingStore((s) => s.backtest);
  const isRunning = useMAccountingStore((s) => s.isBacktesting);
  const error = useMAccountingStore((s) => s.backtestError);
  const analysis = useMAccountingStore((s) => s.backtestAnalysis);
  const isAnalysing = useMAccountingStore((s) => s.isAnalysingBacktest);
  const loadYears = useMAccountingStore((s) => s.loadBacktestYears);
  const run = useMAccountingStore((s) => s.runBacktest);
  const analyse = useMAccountingStore((s) => s.analyseBacktest);
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    void loadYears();
  }, [loadYears]);
  useEffect(() => {
    if (year === null && years.length) setYear(years[years.length - 1]);
  }, [years, year]);

  const byCategory = new Map<string, BacktestRow[]>();
  for (const row of result?.rows ?? []) {
    byCategory.set(row.category, [...(byCategory.get(row.category) ?? []), row]);
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h2 className="font-display text-lg font-bold text-gray-900">Backtest</h2>
        <p className="mt-1 text-sm text-gray-600">
          Holds out a filed year, re-runs it as a forecast in the baseline workbook - seeded
          from the year before, produced by the workbook's own forecast rules - and compares
          it with what was filed.
        </p>
      </div>

      {years.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          A backtest needs two filed years: one to hold out and one before it to seed from.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-700">Hold out</span>
          {years.map((y) => (
            <Button key={y} size="sm" variant={y === year ? 'primary' : 'quiet'} onClick={() => setYear(y)}>
              {y}
            </Button>
          ))}
          <Button variant="secondary" disabled={year === null || isRunning} onClick={() => year !== null && void run(year)}>
            {isRunning ? 'Running…' : 'Run backtest'}
          </Button>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {result && (
        <>
          <ul className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            {result.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>

          <div className="flex flex-wrap gap-3">
            {result.mapeByCategory.map((m) => (
              <div key={m.category} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                <div className="text-xs text-gray-500">{m.category} - mean error</div>
                <div className={`mt-1 font-mono text-lg font-semibold ${tone(m.mape)}`}>
                  {(m.mape * 100).toFixed(1)}%
                </div>
                <div className="text-[11px] text-gray-500">{m.count} lines</div>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-right text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 text-left font-semibold">FY{result.holdoutYear}</th>
                  <th className="px-4 py-2 font-semibold">Filed</th>
                  <th className="px-4 py-2 font-semibold">Predicted</th>
                  <th className="px-4 py-2 font-semibold">Error</th>
                </tr>
              </thead>
              <tbody>
                {[...byCategory.entries()].map(([category, rows]) => (
                  <Fragment key={category}>
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {category}
                      </td>
                    </tr>
                    {rows.map((row) => (
                      <tr key={`${category}-${row.label}`} className="border-t border-gray-100">
                        <td className="px-4 py-1.5 text-gray-900">{row.label}</td>
                        <td className="px-4 py-1.5 text-right font-mono text-xs text-gray-900">{money(row.actual)}</td>
                        <td className="px-4 py-1.5 text-right font-mono text-xs text-gray-900">{money(row.predicted)}</td>
                        <td className={`px-4 py-1.5 text-right font-mono text-xs ${tone(row.pctError)}`}>
                          {row.pctError === null ? '-' : `${(row.pctError * 100).toFixed(1)}%`}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">What the errors mean</div>
              <Button size="sm" variant="secondary" disabled={isAnalysing} onClick={() => void analyse()}>
                {isAnalysing ? 'Reading…' : analysis ? 'Read again' : 'Ask AI'}
              </Button>
            </div>
            {analysis && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <Markdown>{analysis}</Markdown>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
